/**
 * ChatListItem Component
 * Singola voce nella lista chat con context menu
 */

import { memo, useState, useCallback } from 'react';
import { Check, CheckCheck, Image, Mic, FileText, Video, Sparkles, Building2, Bot, FolderKanban, MoreHorizontal, Pin, BellOff } from 'lucide-react';
import { ChatRowMenu } from './ChatRowMenu';
import { RecontactPicker } from '../chat/RecontactPicker';
import { RecontactRowBadge, type RecontactRowInfo } from './RecontactRowBadge';
import type { MuteOption } from '@/lib/services/whatsapp/chatListRules';
import { lastMessagePreviewText } from '@/lib/services/whatsapp/chatListRules';
import { ChatAvatar } from '../chat/ChatAvatar';
import { TimeAgo } from '../shared/TimeAgo';
import { UnreadBadge } from '../shared/UnreadBadge';
import { LabelBadge } from '../shared/LabelBadge';
import { useProfilePicture } from '@/lib/hooks/whatsapp';
import { formatSenderName } from '@/lib/utils/whatsappHelpers';
import type { ChatOverview } from '@/lib/types/whatsappChat';
import type { ChatLabel } from '@/lib/stores/whatsappChatMetaStore';
import type { ChatAssignment } from '@/lib/types/whatsappAgent';
import { tip } from '@/components/messages/ChatTooltip';

interface ChatListItemProps {
  chat: ChatOverview;
  isSelected: boolean;
  isOnline?: boolean;
  isTyping?: boolean;
  unreadCount?: number;
  labels?: ChatLabel[];
  draft?: string;
  suggestionCount?: number;
  assignment?: ChatAssignment | null;
  onClick: () => void;
  onMarkAsRead?: (chatId: string) => void;
  onMarkAsUnread?: (chatId: string) => void;
  /** Fissa in alto / Archivia / Silenzia (lato CRM). */
  pinned?: boolean;
  archived?: boolean;
  muted?: boolean;
  prefsAvailable?: boolean;
  onTogglePin?: (chatId: string) => void;
  onToggleArchive?: (chatId: string) => void;
  onMute?: (chatId: string, option: MuteOption | null) => void;
  /** Promemoria "Ricontatta" della chat (null = nessuno). */
  recontact?: RecontactRowInfo | null;
  organizationId?: string | null;
}

const MessageTypeIcon = ({ type }: { type?: string }) => {
  const iconClass = 'w-3.5 h-3.5 text-[#787774] flex-shrink-0';
  switch (type) {
    case 'image': return <Image className={iconClass} />;
    case 'video': return <Video className={iconClass} />;
    case 'audio':
    case 'ptt': return <Mic className={iconClass} />;
    case 'document': return <FileText className={iconClass} />;
    default: return null;
  }
};

/**
 * Spunte dell'ultimo messaggio inviato. Lo stato vero arriva solo se la lista lo porta
 * (ack WAHA o status: 1 inviato, 2 consegnato, 3/4 letto); senza, una spunta grigia
 * invece della doppia blu sempre accesa che faceva credere "letto" anche quando non lo era.
 */
type LastMessageWithStatus = { fromMe?: boolean; ack?: number; status?: string };
const MessageStatus = ({ message }: { message?: LastMessageWithStatus }) => {
  if (!message?.fromMe) return null;
  const ack = typeof message.ack === 'number' ? message.ack
    : message.status === 'read' ? 3 : message.status === 'delivered' ? 2 : message.status === 'sent' ? 1 : null;
  if (ack !== null && ack >= 3) return <CheckCheck className="w-3.5 h-3.5 text-[#53BDEB] flex-shrink-0" aria-label="Letto" />;
  if (ack === 2) return <CheckCheck className="w-3.5 h-3.5 text-[#787774] flex-shrink-0" aria-label="Consegnato" />;
  return <Check className="w-3.5 h-3.5 text-[#787774] flex-shrink-0" aria-label="Inviato" />;
};

export const ChatListItem = memo(function ChatListItem({
  chat,
  isSelected,
  isOnline = false,
  isTyping = false,
  unreadCount = 0,
  labels = [],
  draft,
  suggestionCount = 0,
  assignment,
  onClick,
  onMarkAsRead,
  onMarkAsUnread,
  pinned = false,
  archived = false,
  muted = false,
  prefsAvailable = true,
  onTogglePin,
  onToggleArchive,
  onMute,
  recontact = null,
  organizationId,
}: ChatListItemProps) {
  const lastMessage = chat.lastMessage;
  const hasUnread = unreadCount > 0 && !lastMessage?.fromMe;
  const displayName = chat.name || chat.id?.split('@')[0] || 'Chat sconosciuta';

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [recontactAt, setRecontactAt] = useState<{ x: number; y: number } | null>(null);

  const { profilePicUrl, ref: observerRef } = useProfilePicture(chat.id);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleCloseMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const openMenuFromButton = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setContextMenu({ x: r.right - 210, y: r.bottom + 4 });
  }, []);

  return (
    <div ref={observerRef} className="relative group">
      <button
        onClick={onClick}
        onContextMenu={handleContextMenu}
        className={`
          w-full px-3 py-2.5 flex gap-3 text-left
          transition-colors relative rounded-lg
          ${isSelected
            ? 'bg-[rgba(35,131,226,0.06)] dark:bg-[#2383E2]/15'
            : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'
          }
        `}
      >
        <ChatAvatar
          name={displayName}
          imageUrl={chat.profilePicUrl || profilePicUrl || undefined}
          isGroup={chat.isGroup}
          isOnline={isOnline}
          isTyping={isTyping}
          size="md"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className={`truncate text-sm ${hasUnread ? 'font-semibold' : 'font-medium'} text-[#37352F] dark:text-white`}>
              {displayName}
            </span>
            <span className="flex items-center gap-1 flex-shrink-0">
              {muted && <BellOff className="w-3 h-3 text-[#9B9A97]" aria-label="Silenziata" />}
              {pinned && <Pin className="w-3 h-3 text-[#9B9A97]" aria-label="Fissata in alto" />}
              {recontact && !recontact.due && !recontact.replied && <RecontactRowBadge info={recontact} />}
              {lastMessage && (
                <TimeAgo timestamp={lastMessage.timestamp * 1000} hasUnread={hasUnread && !muted} />
              )}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            {isTyping ? (
              <span className="text-xs text-[#2383E2] italic">Sta scrivendo...</span>
            ) : draft && !isSelected ? (
              <span className="truncate text-xs">
                <span className="text-red-500 font-medium">Bozza: </span>
                <span className="text-[#787774]">{draft}</span>
              </span>
            ) : (
              <>
                <MessageStatus message={lastMessage as LastMessageWithStatus | undefined} />
                <MessageTypeIcon type={lastMessage?.type} />
                <span className={`truncate text-xs ${hasUnread ? 'text-[#37352F] dark:text-white' : 'text-[#787774]'}`}>
                  {chat.isGroup && lastMessage && !lastMessage.fromMe && lastMessage.senderJid && (
                    <span className="font-medium">
                      {formatSenderName(lastMessage.senderJid, lastMessage.senderName)}:{' '}
                    </span>
                  )}
                  {lastMessagePreviewText(lastMessage)}
                </span>
              </>
            )}
            <div className="flex-shrink-0 ml-auto flex items-center gap-1">
              {suggestionCount > 0 && (
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/30" {...tip(`${suggestionCount} suggerimenti AI`)}>
                  <Sparkles className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                </span>
              )}
              <UnreadBadge count={unreadCount} className={muted ? '!bg-[#9B9A97]' : ''} />
            </div>
          </div>

          {recontact && (recontact.due || recontact.replied) && (
            <div className="flex mt-1"><RecontactRowBadge info={recontact} /></div>
          )}

          {/* Label pills */}
          {labels.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {labels.slice(0, 3).map((label) => (
                <LabelBadge key={label.id} name={label.name} color={label.color} />
              ))}
            </div>
          )}

          {/* Assignment indicators */}
          {assignment && (
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {assignment.client?.business_name && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 max-w-[120px]" {...tip(assignment.client.business_name)}>
                  <Building2 className="w-2.5 h-2.5 flex-shrink-0" />
                  <span className="truncate">{assignment.client.business_name}</span>
                </span>
              )}
              {/* Fase: pipeline_stage ha priorità, altrimenti client_state */}
              {(assignment.client?.pipeline_stage || assignment.client?.client_state) && (() => {
                const stage = assignment.client?.pipeline_stage || assignment.client?.client_state;
                if (!stage) return null;
                const stageColor = stage.color || '#787774';
                return (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium max-w-[100px]"
                    style={{
                      backgroundColor: `${stageColor}18`,
                      color: stageColor,
                      border: `1px solid ${stageColor}30`,
                    }}
                    {...tip(stage.name)}
                  >
                    <span className="truncate">{stage.name}</span>
                  </span>
                );
              })()}
              {assignment.project?.name && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 max-w-[100px]" {...tip(assignment.project.name)}>
                  <FolderKanban className="w-2.5 h-2.5 flex-shrink-0" />
                  <span className="truncate">{assignment.project.name}</span>
                </span>
              )}
              {assignment.agent_response_mode !== 'off' && (
                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                  assignment.agent_response_mode === 'auto'
                    ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
                    : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                }`} {...tip(`AI: ${assignment.agent_response_mode === 'auto' ? 'Automatico' : 'Suggerimenti'}`)}>
                  <Bot className="w-2.5 h-2.5 flex-shrink-0" />
                  {assignment.agent_response_mode === 'auto' ? 'Auto' : 'AI'}
                </span>
              )}
            </div>
          )}
        </div>
      </button>

      {/* Pulsante "..." visibile al passaggio del mouse: stesse voci del tasto destro */}
      <button
        type="button"
        onClick={openMenuFromButton}
        aria-label="Altre azioni sulla chat"
        {...tip('Ricontatta, fissa, archivia, silenzia e altro')}
        className={`absolute right-2 bottom-2 p-1 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-[rgba(55,53,47,0.09)] dark:border-white/10 text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-opacity ${contextMenu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
      >
        <MoreHorizontal className="w-3.5 h-3.5" />
      </button>

      {contextMenu && (
        <ChatRowMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={handleCloseMenu}
          hasUnread={hasUnread}
          onMarkAsRead={() => onMarkAsRead?.(chat.id)}
          onMarkAsUnread={() => onMarkAsUnread?.(chat.id)}
          pinned={pinned}
          archived={archived}
          muted={muted}
          prefsAvailable={prefsAvailable}
          onTogglePin={() => onTogglePin?.(chat.id)}
          onToggleArchive={() => onToggleArchive?.(chat.id)}
          onMute={(opt) => onMute?.(chat.id, opt)}
          onRecontact={organizationId ? () => setRecontactAt(contextMenu) : undefined}
          hasRecontact={!!recontact}
        />
      )}

      {recontactAt && (
        <RecontactPicker x={recontactAt.x} y={recontactAt.y} chatKey={chat.id} chatName={displayName} organizationId={organizationId} onClose={() => setRecontactAt(null)} />
      )}
    </div>
  );
});
